import { supabase } from './supabase'

export interface JobHoursTimeEntry {
  clock_in_time: string
  clock_out_time?: string | null
  total_hours?: number | null
  entry_type?: string | null
}

function roundHours(hours: number): number {
  return Math.round(hours * 100) / 100
}

/** Job billable hours come from job segments only — not day punch or travel. */
export function isJobHourEntry(entry: { entry_type?: string | null }): boolean {
  const t = entry.entry_type
  return t === 'job' || t == null || t === ''
}

/** Hours for one time_entries row. Open rows use `now` as clock-out. */
export function hoursForTimeEntry(
  entry: JobHoursTimeEntry,
  now: Date = new Date()
): number {
  const start = new Date(entry.clock_in_time).getTime()
  if (!Number.isFinite(start)) return 0

  if (entry.clock_out_time) {
    const stored = Number(entry.total_hours)
    if (Number.isFinite(stored) && stored > 0) return roundHours(stored)
    const end = new Date(entry.clock_out_time).getTime()
    if (!Number.isFinite(end) || end <= start) return 0
    return roundHours((end - start) / (1000 * 60 * 60))
  }

  const elapsed = now.getTime() - start
  if (elapsed <= 0) return 0
  return roundHours(elapsed / (1000 * 60 * 60))
}

export function sumJobTimeEntryHours(
  entries: JobHoursTimeEntry[],
  now: Date = new Date()
): number {
  const total = entries
    .filter(isJobHourEntry)
    .reduce((sum, entry) => sum + hoursForTimeEntry(entry, now), 0)
  return roundHours(total)
}

/**
 * Persist hours_actual from time entries. Never writes 0 when the caller
 * would be replacing real elapsed time — returns null to omit the column.
 */
export function hoursActualToPersist(
  computed: number,
  manual?: number | null,
  autoHours = false
): number | null {
  if (autoHours) return computed > 0 ? computed : null
  const entered = Number(manual)
  if (Number.isFinite(entered) && entered > 0) return roundHours(entered)
  return computed > 0 ? computed : null
}

export async function fetchSummedJobActualHours(
  jobId: string,
  now: Date = new Date()
): Promise<number> {
  const { data, error } = await supabase
    .from('time_entries')
    .select('clock_in_time, clock_out_time, total_hours, entry_type')
    .eq('job_id', jobId)
  if (error) throw error
  return sumJobTimeEntryHours((data || []) as JobHoursTimeEntry[], now)
}

/** Write hours_actual from job time_entries. Skips the update when hours are 0. */
export async function stampJobHoursActualFromTimeEntries(jobId: string): Promise<number> {
  const hours = await fetchSummedJobActualHours(jobId)
  if (hours > 0) {
    const { error } = await supabase.from('jobs').update({ hours_actual: hours }).eq('id', jobId)
    if (error) throw error
  }
  return hours
}
